import { Injectable } from '@angular/core';

/** Resolves centered modal across lazy-loaded chunks (student app only). */
export const STUDENT_MESSAGE_MODAL_REGISTRY_KEY = '__studentMessageModalBox';

/** Resolves toast component across lazy-loaded chunks (student app only). */
export const STUDENT_NOTIFICATION_REGISTRY_KEY = '__studentNotificationComp';

@Injectable({ providedIn: 'root' })
export class StudentMessagingService {
  messageBox: any;
  private _ntsComp: any;
  private pendingNotifications: Array<() => void> = [];
  private pendingModalOpens: string[] = [];

  private resolveMessageBox(): any {
    if (this.messageBox != null) {
      return this.messageBox;
    }
    return (window as any)[STUDENT_MESSAGE_MODAL_REGISTRY_KEY] ?? null;
  }

  private resolveNotificationComp(): any {
    if (this._ntsComp != null) {
      return this._ntsComp;
    }
    return (window as any)[STUDENT_NOTIFICATION_REGISTRY_KEY] ?? null;
  }

  private publishMessageBox(box: any): void {
    if (box == null) {
      return;
    }
    this.messageBox = box;
    (window as any)[STUDENT_MESSAGE_MODAL_REGISTRY_KEY] = box;
  }

  private publishNotificationComp(comp: any): void {
    if (comp == null) {
      return;
    }
    this._ntsComp = comp;
    (window as any)[STUDENT_NOTIFICATION_REGISTRY_KEY] = comp;
  }

  private runWithNts(action: () => void): void {
    const nts = this.resolveNotificationComp();
    if (nts != null) {
      action();
      return;
    }
    this.pendingNotifications.push(action);
  }

  private flushPendingNotifications(): void {
    if (this.resolveNotificationComp() == null || this.pendingNotifications.length === 0) {
      return;
    }
    const pending = this.pendingNotifications.slice();
    this.pendingNotifications = [];
    pending.forEach((action) => action());
  }

  private flushPendingModalOpens(): void {
    const box = this.resolveMessageBox();
    if (box == null || this.pendingModalOpens.length === 0) {
      return;
    }
    const pending = this.pendingModalOpens.slice();
    this.pendingModalOpens = [];
    pending.forEach((msg) => box.open(msg));
  }

  info(msg: string): void {
    this.runWithNts(() => this.resolveNotificationComp().info('Information', msg));
  }

  infohtml(msg: string): void {
    this.runWithNts(() => this.resolveNotificationComp().infohtml('Information', msg));
  }

  warn(msg: string, timeout?: number): void {
    this.runWithNts(() => this.nts.warning('Warning', msg, timeout));
  }

  show(msg: string, isInfo?: boolean): void {
    this.runWithNts(() => {
      const nts = this.resolveNotificationComp();
      if (isInfo == undefined || isInfo == true) {
        nts.info('Information', msg);
      } else {
        nts.error('Error', msg);
      }
    });
  }

  showWithTitle(title: string, msg: string, isInfo?: boolean): void {
    this.runWithNts(() => {
      const nts = this.resolveNotificationComp();
      if (isInfo == undefined || isInfo == true) {
        nts.info(title, msg);
      } else {
        nts.error(title, msg);
      }
    });
  }

  error(msg: string): void {
    this.runWithNts(() => this.resolveNotificationComp().error('Error', msg));
  }

  errorWithTitle(title: string, msg: string): void {
    this.runWithNts(() => this.resolveNotificationComp().error(title, msg));
  }

  open(msg: string, isInfo?: boolean): void {
    const box = this.resolveMessageBox();
    if (box != null) {
      box.open(msg);
      return;
    }
    if (msg != null && msg !== '') {
      this.pendingModalOpens.push(msg);
    }
  }

  showWithTimeout(msg: string, callback: any, owner: any, isInfo?: boolean): void {
    this.runWithNts(() => {
      const nts = this.resolveNotificationComp();
      if (isInfo == undefined || isInfo == true) {
        nts.info('Information', msg);
      } else {
        nts.error('Error', msg);
      }
      callback.call(owner);
    });
  }

  showWithTimeoutWithTime(msg: string, callback: any, owner: any, time: number, isInfo?: boolean): void {
    this.runWithNts(() => {
      const nts = this.resolveNotificationComp();
      if (isInfo == undefined || isInfo == true) {
        nts.info('Information', msg, time);
      } else {
        nts.error('Error', msg, time);
      }
      callback.call(owner);
    });
  }

  setBox(box: any): void {
    if (box == null) {
      return;
    }
    this.publishMessageBox(box);
    this.flushPendingModalOpens();
  }

  getBox(): any {
    return this.resolveMessageBox();
  }

  set nts(nt: any) {
    this.publishNotificationComp(nt);
    this.flushPendingNotifications();
  }

  get nts(): any {
    return this.resolveNotificationComp();
  }
}
